import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X, Loader2, Check, FileDown, CalendarCheck, ShieldAlert, Share2, Beer, Wine, GlassWater, AlertTriangle, Salad, Dumbbell, HeartPulse, Microscope, TrendingUp, Sparkles, Pill, Star, PhoneCall, MessageCircle } from "lucide-react";
import jsPDF from "jspdf";
import { buildLiverReport } from "@/lib/pdfReport";
import { LOGO_PNG_B64 } from "@/lib/logoAsset";
import { QUESTIONS, RISK_BANDS } from "@/lib/questions";
import { computeLiverAge, LIVER_AGE_DISCLAIMER } from "@/lib/liverAge";
import { computeTargetWeight } from "@/lib/bmiTarget";
import { recoveryPotential } from "@/lib/recoveryPotential";
import { INDIAN_STATES, CITIES_BY_STATE, ALL_CITIES } from "@/lib/indiaLocations";
import { submitAssessment, sendOtp, verifyOtp, getImpactStats } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import LocationAutocomplete from "@/components/LocationAutocomplete";

const TOTAL = QUESTIONS.length;

// ---------------------------------------------------------------------------
// Business contact + WhatsApp deep-link config (update in one place)
// ---------------------------------------------------------------------------
const CONTACT_PHONE_DISPLAY = "+91 78619 17281";
const WHATSAPP_NUMBER = "917861917281"; // digits only for wa.me

// Band → CTA config. Null = no booking CTA (green).
const CTA_BY_BAND = {
  green: null,
  yellow: { action: "Book Basic Precision Liver Evaluation", short: "Basic Precision Liver Evaluation" },
  orange: { action: "Book Advance Precision Liver Evaluation", short: "Advance Precision Liver Evaluation" },
  red: { action: "Book Priority Consultation", short: "Priority Consultation" },
};

const ageLabel = (id) => ({ a: "Under 35", b: "35-44", c: "45-54", d: "55+" }[id] || "—");
const ageDisplay = (contact, answers) => {
  const y = Number(contact?.ageYears);
  if (Number.isFinite(y) && y > 0) return String(Math.round(y));
  return ageLabel(answers?.age);
};

const buildWhatsAppUrl = (result, contact, answers) => {
  const cta = CTA_BY_BAND[result.band];
  if (!cta) return null;
  const fullName = `${contact.firstName} ${contact.lastName}`.trim() || "Not shared";
  const age = ageDisplay(contact, answers);
  const sex = contact.gender || "Not shared";
  const phone = contact.phone || "Not shared";
  const shortId = String(result.id || "").split("-")[0].toUpperCase();
  const lines = [
    `Hi SuperAge Health,`,
    ``,
    `I just completed the Liver Risk Assessment and would like to *${cta.short}*.`,
    ``,
    `Please find my details below:`,
    `• Name: ${fullName}`,
    `• Age: ${age} yrs`,
    `• Sex: ${sex}`,
    `• Phone: ${phone}`,
    ``,
    `Report ID: ${shortId}`,
    `Liver Risk Score: ${result.score}/100`,
    `Risk Band: ${result.label}`,
    ``,
    `Please call me back to coordinate.`,
    `Thank you.`,
  ];
  const text = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
};

export default function Assessment() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("splash"); // splash | intro | questions | otp | loading | result
  const [language, setLanguage] = useState("en"); // en | hi | gu — assessment language
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ firstName: "", lastName: "", gender: "", state: "", city: "", email: "", phone: "" });
  const [result, setResult] = useState(null);

  const current = QUESTIONS[index];
  const answered = answers[current?.id];
  const isMulti = current?.type === "multiselect";
  const hasAnswer = isMulti
    ? Array.isArray(answered) && answered.length > 0
    : Boolean(answered);
  const progress =
    phase === "result" ? 100 : (phase === "splash" || phase === "intro") ? 0 : Math.round(((index + 1) / TOTAL) * 100);

  const select = (optId) => {
    setAnswers((a) => ({ ...a, [current.id]: optId }));
  };

  const toggleMulti = (opt) => {
    setAnswers((a) => {
      const prev = Array.isArray(a[current.id]) ? a[current.id] : [];
      // "exclusive" option (like "none") clears everything else on select
      if (opt.exclusive) {
        return { ...a, [current.id]: prev.includes(opt.id) ? [] : [opt.id] };
      }
      // Selecting any non-exclusive option removes the exclusive one
      const cleaned = prev.filter((id) => {
        const o = current.options.find((x) => x.id === id);
        return o && !o.exclusive;
      });
      const next = cleaned.includes(opt.id)
        ? cleaned.filter((id) => id !== opt.id)
        : [...cleaned, opt.id];
      return { ...a, [current.id]: next };
    });
  };

  const startQuestions = () => {
    setDirection(1);
    setIndex(0);
    setPhase("questions");
  };

  const next = useCallback(() => {
    if (isMulti) {
      if (!hasAnswer) return;
    } else if (!answers[current.id]) {
      return;
    }
    if (index < TOTAL - 1) {
      setDirection(1);
      setIndex((i) => i + 1);
    } else {
      goToOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, current, index, isMulti, hasAnswer]);

  const back = () => {
    if (phase === "otp") {
      setPhase("questions");
      setIndex(TOTAL - 1);
      return;
    }
    if (index > 0) {
      setDirection(-1);
      setIndex((i) => i - 1);
    } else if (phase === "questions") {
      setPhase("intro");
    } else {
      navigate("/");
    }
  };

  const goToOtp = async () => {
    setPhase("otp");
    try {
      const resp = await sendOtp(contact.phone);
      if (resp.dev_code) {
        // Dev-only visibility — remove once real SMS provider is wired in
        toast.success(`Dev OTP: ${resp.dev_code}`);
      } else {
        toast.success("Code sent to your phone");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code. Please try again.");
    }
  };

  const verifyAndCompute = async (code) => {
    setPhase("otp"); // stay
    try {
      await verifyOtp(contact.phone, code);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Incorrect code. Please try again.";
      toast.error(msg);
      throw e;
    }
    // Phone verified — compute
    compute();
  };

  const compute = async () => {
    setPhase("loading");
    try {
      // Separate red_flags from the scored responses
      const { redflags, ...responses } = answers;
      const redFlagsArr = Array.isArray(redflags)
        ? redflags.filter((id) => id !== "none")
        : [];
      const data = await submitAssessment({
        responses,
        red_flags: redFlagsArr,
        name: `${contact.firstName} ${contact.lastName}`.trim() || null,
        email: contact.email || null,
        phone: contact.phone || null,
        gender: contact.gender || null,
        state: contact.state || null,
        city: contact.city || null,
        bmi_value: contact.bmiValue || null,
        height_cm: contact.heightCm || null,
        weight_kg: contact.weightKg || null,
        age_years: Number(contact.ageYears) || null,
      });
      setResult(data);
      // Remember contact details so /book can autofill name/email/phone.
      try {
        sessionStorage.setItem("sa_contact", JSON.stringify({
          name: `${contact.firstName} ${contact.lastName}`.trim(),
          email: contact.email || "",
          phone: contact.phone || "",
        }));
      } catch {}
      setPhase("result");
    } catch (e) {
      toast.error("Could not calculate your score. Please try again.");
      setPhase("questions");
      setIndex(TOTAL - 1);
    }
  };

  const downloadReport = () => {
    if (!result) return;
    const { doc, shortId } = buildLiverReport(result, contact, answers);
    doc.save(`SuperAge-Liver-Risk-Report-${shortId}.pdf`);
    toast.success("Your report has been downloaded.");
  };

  const shareScore = async () => {
    if (!result) return;
    const band = RISK_BANDS[result.band];
    const size = 1080;
    const heightPx = 1920;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = heightPx;
    const ctx = canvas.getContext("2d");

    // ---------- Palette (matches PDF report) ----------
    const CANVAS_BG = "#FBF9F4";
    const BEIGE = "#EBE8E0";
    const FOREST = "#0A2E22";
    const COPPER = "#C67B5C";
    const MUTED = "#5A6460";

    // Cream background with a soft warm gradient
    const grad = ctx.createLinearGradient(0, 0, 0, heightPx);
    grad.addColorStop(0, CANVAS_BG);
    grad.addColorStop(1, "#F0EDE3");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, heightPx);

    // Subtle grain texture — 900 tiny beige dots across the taller canvas
    ctx.fillStyle = "rgba(198,123,92,0.05)";
    for (let i = 0; i < 900; i++) {
      const x = Math.random() * size;
      const y = Math.random() * heightPx;
      ctx.fillRect(x, y, 1, 1);
    }

    // ---------- Header: Logo + wordmark ----------
    const logoImg = new Image();
    logoImg.src = LOGO_PNG_B64.startsWith("data:") ? LOGO_PNG_B64 : `data:image/png;base64,${LOGO_PNG_B64}`;
    await new Promise((res) => { logoImg.onload = res; logoImg.onerror = res; });
    // Centered brand lockup at the top: logo + "SuperAge Health" + tagline
    const logoH = 92;
    const logoW = logoH * (118 / 82);
    const headerY = 130;
    // Measure the wordmark width to compute the total lockup width
    ctx.font = "700 60px Georgia, serif";
    const superAgeMeasure = ctx.measureText("SuperAge ").width;
    const healthMeasure = ctx.measureText("Health").width;
    const wordmarkW = superAgeMeasure + healthMeasure;
    const gapAfterLogo = 20;
    const totalW = logoW + gapAfterLogo + wordmarkW;
    const lockupLeft = (size - totalW) / 2;
    // Draw logo
    ctx.drawImage(logoImg, lockupLeft, headerY - logoH / 2 - 6, logoW, logoH);
    // Draw wordmark
    ctx.textAlign = "left";
    const wordX = lockupLeft + logoW + gapAfterLogo;
    ctx.fillStyle = FOREST;
    ctx.fillText("SuperAge", wordX, headerY + 20);
    ctx.fillStyle = COPPER;
    ctx.fillText("Health", wordX + superAgeMeasure, headerY + 20);
    // Tagline — centered under the wordmark with small dashes flanking
    ctx.textAlign = "center";
    ctx.fillStyle = MUTED;
    ctx.font = "500 22px Arial";
    const taglineText = "PRECISION ORGAN MEDICINE";
    const taglineCX = size / 2;
    const taglineY = headerY + 66;
    ctx.fillText(taglineText, taglineCX, taglineY);
    // Dashes on either side of the tagline
    const taglineWidth = ctx.measureText(taglineText).width;
    const dashLen = 26;
    const dashGap = 16;
    ctx.strokeStyle = MUTED;
    ctx.lineWidth = 2;
    const dashY = taglineY - 7;
    ctx.beginPath();
    ctx.moveTo(taglineCX - taglineWidth / 2 - dashGap - dashLen, dashY);
    ctx.lineTo(taglineCX - taglineWidth / 2 - dashGap, dashY);
    ctx.moveTo(taglineCX + taglineWidth / 2 + dashGap, dashY);
    ctx.lineTo(taglineCX + taglineWidth / 2 + dashGap + dashLen, dashY);
    ctx.stroke();

    // Divider line
    ctx.strokeStyle = BEIGE;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(120, 250);
    ctx.lineTo(size - 120, 250);
    ctx.stroke();

    // ---------- Report title ----------
    ctx.textAlign = "center";
    ctx.fillStyle = COPPER;
    ctx.font = "700 22px Arial";
    ctx.fillText("YOUR LIVER RISK SCORE", size / 2, 258);

    // ---------- Big gauge ----------
    const cx = size / 2;
    const cy = 552;
    const r = 200;
    // Track
    ctx.strokeStyle = BEIGE;
    ctx.lineWidth = 40;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    // Progress
    ctx.strokeStyle = band.hex;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (result.score / 100) * Math.PI * 2);
    ctx.stroke();
    ctx.lineCap = "butt";

    // Score number
    ctx.fillStyle = FOREST;
    ctx.font = "700 190px Georgia, serif";
    ctx.fillText(String(result.score), cx, cy + 62);
    ctx.font = "500 28px Arial";
    ctx.fillStyle = MUTED;
    ctx.fillText("/ 100", cx, cy + 110);

    // Band pill
    const pillW = 460;
    const pillH = 82;
    const pillX = cx - pillW / 2;
    const pillY = 810;
    const rr = 41;
    ctx.fillStyle = band.hex;
    ctx.beginPath();
    ctx.moveTo(pillX + rr, pillY);
    ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, rr);
    ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, rr);
    ctx.arcTo(pillX, pillY + pillH, pillX, pillY, rr);
    ctx.arcTo(pillX, pillY, pillX + pillW, pillY, rr);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 34px Arial";
    ctx.fillText(result.label.toUpperCase(), cx, pillY + 54);

    // Helper: rounded rectangle
    const roundedRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    };

    // ===================================================
    //  Additional cards (added below the score gauge)
    //  Positions are absolute Y-coords within the 1920px canvas.
    // ===================================================
    const cardX = 90;
    const cardW = size - cardX * 2;
    ctx.textAlign = "left";

    // ---------- Recovery Potential card (y ≈ 940) ----------
    const rp = recoveryPotential(result.score, result.band);
    if (rp) {
      const yTop = 940;
      const cardH = 130;
      ctx.fillStyle = "#FFFFFF";
      roundedRect(cardX, yTop, cardW, cardH, 24);
      ctx.fill();
      ctx.strokeStyle = BEIGE;
      ctx.lineWidth = 2;
      roundedRect(cardX, yTop, cardW, cardH, 24);
      ctx.stroke();
      ctx.fillStyle = COPPER;
      ctx.font = "700 22px Arial";
      ctx.fillText("RECOVERY POTENTIAL", cardX + 30, yTop + 40);
      // Draw stars
      const starY = yTop + 82;
      const starX0 = cardX + 30;
      const starR = 18;
      const drawStarPath = (cxs, cys, outerR, innerR) => {
        ctx.beginPath();
        for (let i = 0; i < 10; i++) {
          const angle = (Math.PI / 5) * i - Math.PI / 2;
          const rr = i % 2 === 0 ? outerR : innerR;
          const px = cxs + Math.cos(angle) * rr;
          const py = cys + Math.sin(angle) * rr;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
      };
      for (let i = 0; i < 5; i++) {
        const filled = i < rp.stars;
        drawStarPath(starX0 + i * 44 + starR, starY, starR, starR * 0.4);
        if (filled) {
          ctx.fillStyle = COPPER;
          ctx.fill();
        } else {
          ctx.strokeStyle = BEIGE;
          ctx.lineWidth = 3;
          ctx.stroke();
        }
      }
      // Label on the right
      ctx.textAlign = "right";
      ctx.fillStyle = FOREST;
      ctx.font = "700 40px Georgia, serif";
      ctx.fillText(rp.label, cardX + cardW - 30, starY + 12);
      ctx.textAlign = "left";
    }

    // ---------- SuperAge Liver Age card (y ≈ 1100) ----------
    const liverAgeData = computeLiverAge(result.score, contact?.ageYears ?? contact?.age_years);
    if (liverAgeData) {
      const yTop = 1100;
      const cardH = 200;
      // Cream forest gradient card
      ctx.fillStyle = "#FFFFFF";
      roundedRect(cardX, yTop, cardW, cardH, 24);
      ctx.fill();
      ctx.strokeStyle = BEIGE;
      ctx.lineWidth = 2;
      roundedRect(cardX, yTop, cardW, cardH, 24);
      ctx.stroke();
      ctx.fillStyle = COPPER;
      ctx.font = "700 22px Arial";
      ctx.textAlign = "center";
      ctx.fillText("SUPERAGE LIVER AGE\u2122", size / 2, yTop + 38);
      // Chrono → Liver age
      ctx.font = "700 90px Georgia, serif";
      ctx.fillStyle = FOREST;
      ctx.textAlign = "center";
      ctx.fillText(String(liverAgeData.chrono), cardX + cardW * 0.22, yTop + 138);
      ctx.font = "500 24px Arial";
      ctx.fillStyle = MUTED;
      ctx.fillText("Chronological", cardX + cardW * 0.22, yTop + 176);
      // Arrow / delta pill in center
      const deltaText = liverAgeData.penalty === 0 ? "On track" : `+${liverAgeData.penalty} yrs`;
      const deltaColor = liverAgeData.penalty === 0 ? "#22C55E" : band.hex;
      ctx.font = "700 26px Arial";
      const dw = ctx.measureText(deltaText).width + 40;
      const dpx = cardX + cardW * 0.5 - dw / 2;
      const dpy = yTop + 116;
      ctx.fillStyle = deltaColor;
      roundedRect(dpx, dpy, dw, 44, 22);
      ctx.fill();
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText(deltaText, cardX + cardW * 0.5, dpy + 30);
      // Liver age (right)
      ctx.font = "700 90px Georgia, serif";
      ctx.fillStyle = deltaColor;
      ctx.fillText(`~${liverAgeData.liverAge}`, cardX + cardW * 0.78, yTop + 138);
      ctx.font = "500 24px Arial";
      ctx.fillStyle = MUTED;
      ctx.fillText("Liver Age (yrs)", cardX + cardW * 0.78, yTop + 176);
      ctx.textAlign = "left";
    }

    // ---------- BMI + Target Weight card (y ≈ 1330) ----------
    const bmiValue = contact?.bmiValue ?? contact?.bmi_value;
    if (bmiValue != null) {
      const yTop = 1330;
      const cardH = 200;
      ctx.fillStyle = "#FFFFFF";
      roundedRect(cardX, yTop, cardW, cardH, 24);
      ctx.fill();
      ctx.strokeStyle = BEIGE;
      ctx.lineWidth = 2;
      roundedRect(cardX, yTop, cardW, cardH, 24);
      ctx.stroke();
      // Left: BMI value + band
      ctx.fillStyle = COPPER;
      ctx.font = "700 22px Arial";
      ctx.textAlign = "left";
      ctx.fillText("BODY MASS INDEX", cardX + 30, yTop + 40);
      ctx.fillStyle = FOREST;
      ctx.font = "700 90px Georgia, serif";
      ctx.fillText(bmiValue.toFixed(1), cardX + 30, yTop + 130);
      // BMI band pill
      const bmiBands = [
        { min: 0, max: 18.5, label: "Under", hex: "#8EB4E3" },
        { min: 18.5, max: 23, label: "Normal", hex: "#22C55E" },
        { min: 23, max: 25, label: "Over", hex: "#EAB308" },
        { min: 25, max: 30, label: "Obese", hex: "#F97316" },
        { min: 30, max: 999, label: "Morbid", hex: "#EF4444" },
      ];
      const bBand = bmiBands.find((b) => bmiValue >= b.min && bmiValue < b.max) || bmiBands[bmiBands.length - 1];
      ctx.font = "700 24px Arial";
      const bpW = ctx.measureText(bBand.label.toUpperCase()).width + 36;
      ctx.fillStyle = bBand.hex;
      roundedRect(cardX + 30, yTop + 150, bpW, 40, 20);
      ctx.fill();
      ctx.fillStyle = "#FFFFFF";
      ctx.textAlign = "center";
      ctx.fillText(bBand.label.toUpperCase(), cardX + 30 + bpW / 2, yTop + 178);
      // Right: Target weight
      const heightCm = contact?.heightCm ?? contact?.height_cm;
      const weightKg = contact?.weightKg ?? contact?.weight_kg;
      const tgt = computeTargetWeight(heightCm, weightKg);
      if (tgt) {
        const rx = cardX + cardW - 30;
        ctx.textAlign = "right";
        ctx.fillStyle = COPPER;
        ctx.font = "700 22px Arial";
        ctx.fillText("TARGET WEIGHT", rx, yTop + 40);
        ctx.fillStyle = FOREST;
        ctx.font = "700 68px Georgia, serif";
        ctx.fillText(`~${tgt.targetKg} kg`, rx, yTop + 118);
        // Action label
        ctx.font = "700 22px Arial";
        const verb = tgt.action === "lose" ? `Lose ~${tgt.deltaKg} kg` : tgt.action === "gain" ? `Gain ~${tgt.deltaKg} kg` : "Maintain";
        const actionColor = tgt.action === "maintain" ? "#22C55E" : band.hex;
        ctx.fillStyle = actionColor;
        const aw = ctx.measureText(verb).width + 36;
        roundedRect(rx - aw, yTop + 148, aw, 40, 20);
        ctx.fill();
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        ctx.fillText(verb, rx - aw / 2, yTop + 176);
      }
      ctx.textAlign = "left";
    }

    // ---------- Next Step card (y ≈ 1570) — forest CTA ----------
    {
      const yTop = 1570;
      const cardH = 220;
      ctx.fillStyle = FOREST;
      roundedRect(cardX, yTop, cardW, cardH, 24);
      ctx.fill();
      ctx.fillStyle = COPPER;
      ctx.font = "700 22px Arial";
      ctx.textAlign = "left";
      ctx.fillText("YOUR NEXT STEP", cardX + 40, yTop + 46);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "700 44px Georgia, serif";
      const nextTitle =
        result.band === "green"
          ? "Stay ahead. Maintain."
          : result.band === "yellow"
          ? "Advance Precision Assessment"
          : "Book your Advance Precision Liver Evaluation";
      // Wrap the next step title so it fits card width
      const words = nextTitle.split(" ");
      let line = "";
      let ny = yTop + 100;
      const maxW = cardW - 80;
      for (const w of words) {
        const test = line ? line + " " + w : w;
        if (ctx.measureText(test).width > maxW) {
          ctx.fillText(line, cardX + 40, ny);
          line = w;
          ny += 52;
        } else line = test;
      }
      if (line) ctx.fillText(line, cardX + 40, ny);
      ctx.textAlign = "left";
    }

    // ---------- Footer tagline (bottom of the 1920 canvas) ----------
    ctx.textAlign = "center";
    ctx.fillStyle = COPPER;
    ctx.font = "italic 34px Georgia, serif";
    ctx.fillText("Assess. Restore. Sustain.", cx, 1850);
    ctx.fillStyle = MUTED;
    ctx.font = "500 22px Arial";
    ctx.fillText("Know your liver risk in 2 minutes  \u00b7  superagehealth.com", cx, 1890);
    ctx.textAlign = "left";

    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    const file = new File([blob], "my-superage-liver-report.png", { type: "image/png" });
    const lp = liverAgeData ? ` · Liver Age ~${liverAgeData.liverAge}` : "";
    const rpTxt = rp ? ` · Recovery ${rp.label}` : "";
    const shareText = `My SuperAge Liver Risk Score is ${result.score}/100 (${result.label})${lp}${rpTxt}. Know your liver risk in 2 minutes: https://superagehealth.com`;

    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My SuperAge Liver Report", text: shareText });
        return;
      }
    } catch (e) {
      // fall through to WhatsApp Web + download
    }

    // Fallback: download the image AND open WhatsApp Web with the pre-filled
    // text — user can attach the freshly-downloaded image with one click.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my-superage-liver-report.png";
    a.click();
    URL.revokeObjectURL(url);
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
    toast.success("Report image downloaded — attach it to WhatsApp");
  };

  return (
    <div className="min-h-screen bg-sa-beige" data-testid="assessment-page">
      {/* Top bar */}
      <div className="fixed inset-x-0 top-0 z-40 bg-sa-beige/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-6 py-4">
          <button onClick={back} className="text-sa-forest transition-colors hover:text-sa-copper" data-testid="assessment-back" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white">
            <motion.div
              className="h-full rounded-full bg-sa-copper"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid="assessment-progress"
            />
          </div>
          <span className="w-10 text-right text-sm font-medium text-sa-muted">{progress}%</span>
          <button onClick={() => navigate("/")} className="text-sa-forest transition-colors hover:text-sa-copper" data-testid="assessment-close" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="mx-auto flex min-h-screen max-w-3xl items-center px-6 pb-16 pt-24">
        <div className="w-full">
          <AnimatePresence mode="wait" custom={direction}>
            {phase === "splash" && (
              <SplashCard
                key="splash"
                language={language}
                onLanguageChange={setLanguage}
                onStart={() => setPhase("intro")}
              />
            )}

            {phase === "intro" && (
              <IntroCard
                key="intro"
                contact={contact}
                setContact={setContact}
                onStart={startQuestions}
              />
            )}

            {phase === "questions" && (
              <QuestionCard
                key={current.id}
                language={language}
                direction={direction}
                q={current}
                options={
                  current.optionsByGender
                    ? current.optionsByGender[contact.gender === "Female" ? "female" : "male"]
                    : current.options
                }
                index={index}
                total={TOTAL}
                selected={answered}
                onSelect={select}
                onToggleMulti={toggleMulti}
                canProceed={hasAnswer}
                onNext={next}
                contact={contact}
                setContact={setContact}
              />
            )}

            {phase === "otp" && (
              <OtpCard
                key="otp"
                phone={contact.phone}
                onVerify={verifyAndCompute}
                onResend={async () => {
                  try {
                    const r = await sendOtp(contact.phone);
                    if (r.dev_code) toast.success(`Dev OTP: ${r.dev_code}`);
                    else toast.success("New code sent");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not resend.");
                  }
                }}
                onChangePhone={async (newPhone) => {
                  const cleaned = (newPhone || "").trim();
                  if (!cleaned) throw new Error("Enter a valid phone number");
                  setContact((c) => ({ ...c, phone: cleaned }));
                  const r = await sendOtp(cleaned);
                  if (r.dev_code) toast.success(`Dev OTP: ${r.dev_code}`);
                  else toast.success(`New code sent to ${cleaned}`);
                }}
              />
            )}

            {phase === "loading" && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-24 text-center"
                data-testid="assessment-loading"
              >
                <Loader2 className="h-10 w-10 animate-spin text-sa-copper" />
                <p className="mt-6 font-display text-2xl font-medium text-sa-forest">
                  Calculating your Liver Risk Score
                </p>
                <p className="mt-2 text-sa-muted">Analyzing your responses…</p>
              </motion.div>
            )}

            {phase === "result" && result && (
              <ResultCard
                key="result"
                result={result}
                contact={contact}
                answers={answers}
                onDownload={downloadReport}
                onShare={shareScore}
                onBook={() => {
                  // Yellow → Basic, Orange → Advanced: go straight to the
                  // booking page with the right package pre-selected.
                  if (result.band === "yellow" || result.band === "orange") {
                    const tier = result.band === "orange" ? "advanced" : "basic";
                    try { sessionStorage.setItem("sa_risk_band", result.band); } catch {}
                    navigate(`/book?tier=${tier}`);
                    return;
                  }
                  // Red → priority consultation fast-track (no payment).
                  if (result.band === "red") {
                    try { sessionStorage.setItem("sa_risk_band", "red"); } catch {}
                    navigate("/book?priority=1");
                    return;
                  }
                  const url = buildWhatsAppUrl(result, contact, answers);
                  if (url) {
                    window.open(url, "_blank", "noopener,noreferrer");
                  } else {
                    // Green band — no booking needed; scroll to home
                    navigate("/", { state: { scrollTo: "booking" } });
                  }
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
const slide = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

function QuestionCard({ q, language = "en", options, index, total, selected, onSelect, onToggleMulti, canProceed, onNext, direction, contact, setContact }) {
  const opts = options || q.options;
  const isMulti = q.type === "multiselect";
  const selectedArr = Array.isArray(selected) ? selected : [];
  // Language pick — return the localized string if present, else fall back
  // to English so translations can be added incrementally.
  const t = (obj, key) => {
    if (!obj) return "";
    if (language === "hi" && obj[`${key}_hi`]) return obj[`${key}_hi`];
    if (language === "gu" && obj[`${key}_gu`]) return obj[`${key}_gu`];
    return obj[key];
  };
  return (
    <motion.div
      custom={direction}
      variants={slide}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid="question-card"
    >
      <p className="mb-3 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
        {q.category} · {index + 1} of {total}
      </p>
      <h2 className="font-display text-3xl font-medium leading-[1.12] tracking-tight text-sa-forest sm:text-4xl">
        {t(q, "question")}
      </h2>
      <p className="mt-3 text-sa-muted">{t(q, "help")}</p>

      {q.note && <QuestionNote note={q.note} />}

      {q.reference && <ReferenceTable ref_={q.reference} />}

      {q.type === "bmi" ? (
        <BmiInput
          selected={selected}
          onSelect={onSelect}
          onBmiValue={(v) => setContact((c) => ({ ...c, bmiValue: v }))}
          onSizeValue={({ height_cm, weight_kg }) => setContact((c) => ({ ...c, heightCm: height_cm, weightKg: weight_kg }))}
        />
      ) : q.type === "age" ? (
        <AgeInput
          selected={selected}
          onSelect={onSelect}
          value={contact.ageYears}
          onChangeYears={(y) => setContact((c) => ({ ...c, ageYears: y }))}
          min={q.min || 18}
          max={q.max || 100}
        />
      ) : isMulti ? (
        <div className="mt-8 space-y-3" data-testid="multiselect-options">
          {opts.map((o) => {
            const active = selectedArr.includes(o.id);
            const isEmergency = o.emergency;
            const isUrgent = o.urgent;
            return (
              <button
                key={o.id}
                onClick={() => onToggleMulti(o)}
                data-testid={`option-${q.id}-${o.id}`}
                className={`group flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 ${
                  active
                    ? isEmergency
                      ? "border-red-500 bg-white shadow-[0_10px_30px_-12px_rgba(239,68,68,0.5)]"
                      : isUrgent
                      ? "border-orange-400 bg-white shadow-[0_10px_30px_-12px_rgba(249,115,22,0.5)]"
                      : "border-sa-copper bg-white shadow-[0_10px_30px_-12px_rgba(198,123,92,0.5)]"
                    : "border-transparent bg-white/70 hover:bg-white"
                }`}
              >
                <span className="flex items-center gap-3">
                  {(isEmergency || isUrgent) && !o.exclusive && (
                    <AlertTriangle
                      className={`h-4 w-4 flex-none ${isEmergency ? "text-red-500" : "text-orange-500"}`}
                      strokeWidth={2}
                    />
                  )}
                  <span className={`text-[16px] ${active ? "font-medium text-sa-forest" : "text-sa-forest/80"}`}>
                    {t(o, "label")}
                  </span>
                </span>
                <span
                  className={`flex h-6 w-6 flex-none items-center justify-center rounded-md border transition-colors ${
                    active
                      ? isEmergency
                        ? "border-red-500 bg-red-500 text-white"
                        : isUrgent
                        ? "border-orange-500 bg-orange-500 text-white"
                        : "border-sa-copper bg-sa-copper text-white"
                      : "border-sa-sage/50"
                  }`}
                >
                  {active && <Check className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {opts.map((o) => {
            const active = selected === o.id;
            return (
              <button
                key={o.id}
                onClick={() => onSelect(o.id)}
                data-testid={`option-${q.id}-${o.id}`}
                className={`group flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 ${
                  active
                    ? "border-sa-copper bg-white shadow-[0_10px_30px_-12px_rgba(198,123,92,0.5)]"
                    : "border-transparent bg-white/70 hover:bg-white"
                }`}
              >
                <span className={`text-[16px] ${active ? "font-medium text-sa-forest" : "text-sa-forest/80"}`}>
                  {t(o, "label")}
                </span>
                <span
                  className={`flex h-6 w-6 flex-none items-center justify-center rounded-full border transition-colors ${
                    active ? "border-sa-copper bg-sa-copper text-white" : "border-sa-sage/50"
                  }`}
                >
                  {active && <Check className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={onNext}
        disabled={isMulti ? !canProceed : !selected}
        data-testid="question-next"
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-sa-forest px-7 py-3.5 font-medium text-white transition-[transform,background-color,opacity] duration-300 hover:scale-[1.02] hover:bg-sa-forest-soft disabled:cursor-not-allowed disabled:opacity-40"
      >
        {index === total - 1 ? "See my results" : "Continue"}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </motion.div>
  );
}

const NOTE_ICONS = { beer: Beer, wine: Wine, spirit: GlassWater };

function QuestionNote({ note }) {
  return (
    <div
      className="mt-8 rounded-2xl border border-sa-copper/25 bg-sa-copper/[0.06] p-5"
      data-testid="question-note"
    >
      <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
        {note.title}
      </p>
      <p className="mt-2 text-[15px] leading-relaxed text-sa-forest">{note.body}</p>

      {note.visuals && (
        <div className="mt-4 grid grid-cols-3 gap-3">
          {note.visuals.map((v) => {
            const Icon = NOTE_ICONS[v.icon] || GlassWater;
            return (
              <div
                key={v.icon}
                className="flex flex-col items-center gap-2 rounded-xl border border-white bg-white/70 px-3 py-4 text-center"
              >
                <Icon className="h-6 w-6 text-sa-copper" strokeWidth={1.6} />
                <span className="text-[12px] font-medium text-sa-forest/80">{v.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {note.caption && (
        <p className="mt-4 text-[12px] leading-relaxed text-sa-muted">{note.caption}</p>
      )}
    </div>
  );
}

// BMI bands per Indian / WHO Asia-Pacific consensus.
// These cutoffs are stricter than the WHO global standard because South Asians
// carry more visceral fat at lower BMI values.
const BMI_BANDS = [
  { min: 0,    max: 18.5, label: "Underweight",    hex: "#60A5FA" },  // blue
  { min: 18.5, max: 23,   label: "Normal",         hex: "#22C55E" },  // green
  { min: 23,   max: 25,   label: "Overweight",     hex: "#EAB308" },  // yellow
  { min: 25,   max: 30,   label: "Obese",          hex: "#F97316" },  // orange
  { min: 30,   max: 50,   label: "Morbidly Obese", hex: "#EF4444" },  // red
];

const BMI_MIN_DISPLAY = 15;
const BMI_MAX_DISPLAY = 40;

function bmiBandFor(bmi) {
  return BMI_BANDS.find((b) => bmi >= b.min && bmi < b.max) || BMI_BANDS[BMI_BANDS.length - 1];
}

/** SuperAge Liver Age™ — approximate liver-age callout for the results page.
 *  Emotionally hooks the number to a tangible outcome the visitor can feel.
 *  Score-band → years-added logic lives in @/lib/liverAge (shared with PDF).
 */

/** LiverAgeWave — an EKG-style path whose amplitude and rhythm scale with the
 *  risk penalty. Flat = "calm rhythm, on track"; taller peaks = "strain".
 *  Uses framer-motion pathLength to gently draw itself on mount.
 */
function LiverAgeWave({ penalty, bandHex, isFlat }) {
  // Amplitude range: flat (penalty 0) → dramatic (penalty 20)
  const intensity = Math.min(1, Math.max(0, penalty / 20));
  // Base wave amplitude in SVG units. 0 → 3 (nearly flat), 1 → 16 (dramatic).
  const amp = 3 + intensity * 13;
  // Vertical baseline
  const midY = 32;
  // Build a heartbeat/EKG path — quiet start, sharp peak, quiet end
  // Points chosen so path length remains similar across bands.
  const path = isFlat
    ? // Calm sinusoidal breathing rhythm
      `M 4 ${midY} Q 20 ${midY - 2} 36 ${midY} T 68 ${midY} T 100 ${midY} T 132 ${midY}`
    : // Aging rhythm — subtle lead-in, sharper QRS-style spike, tail
      `M 4 ${midY} L 22 ${midY} L 32 ${midY - amp * 0.4} L 42 ${midY + amp * 0.8} L 52 ${midY - amp} L 62 ${midY + amp * 0.5} L 72 ${midY} L 92 ${midY} L 104 ${midY - amp * 0.35} L 116 ${midY + amp * 0.35} L 132 ${midY}`;

  return (
    <motion.svg
      viewBox="0 0 136 64"
      width="136"
      height="52"
      fill="none"
      className="overflow-visible"
      data-testid="liver-age-wave"
      aria-hidden="true"
    >
      {/* Soft baseline for context */}
      <line
        x1="4" y1={midY} x2="132" y2={midY}
        stroke="rgba(10,46,34,0.15)" strokeWidth="1" strokeDasharray="2 3"
      />
      {/* Glow trail */}
      <motion.path
        d={path}
        stroke={bandHex}
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.22"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
      />
      {/* Main line */}
      <motion.path
        d={path}
        stroke={bandHex}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
      />
      {/* Trailing pulse dot on the right */}
      <motion.circle
        cx="132" cy={midY} r="3"
        fill={bandHex}
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: [0, 1, 0.6, 1], scale: [0.4, 1.2, 0.9, 1] }}
        transition={{ duration: 2.2, delay: 1.6, repeat: Infinity, repeatDelay: 1.4 }}
      />
    </motion.svg>
  );
}

function LiverAgeCard({ result, contact }) {
  const chronoAge = contact?.ageYears;
  const data = computeLiverAge(result?.score, chronoAge);
  if (!data) return null;
  const band = RISK_BANDS[result.band] || {};
  const bandHex = band.hex || "#8B5E3C";
  const isFlat = data.penalty === 0;

  return (
    <div className="mt-8" data-testid="liver-age-card">
      <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
        SuperAge Liver Age<span className="align-super text-[9px]">™</span>
      </p>
      <div
        className="mt-4 overflow-hidden rounded-3xl border border-sa-beige"
        style={{
          background:
            "linear-gradient(135deg, #FBF7EC 0%, #F3EEDF 55%, #ECE5D0 100%)",
        }}
      >
        <div className="grid grid-cols-1 items-stretch gap-0 md:grid-cols-[1fr_auto_1fr]">
          {/* Chronological age */}
          <div className="px-6 py-6 md:px-8 md:py-7">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-sa-muted">
              Chronological
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <span
                className="font-display font-medium leading-none text-sa-forest"
                style={{ fontSize: "3rem" }}
                data-testid="liver-age-chrono"
              >
                {data.chrono}
              </span>
              <span className="text-sm text-sa-muted">yrs</span>
            </div>
          </div>

          {/* Animated aging waveform — visually communicates the "drift"
              between chronological and liver age without stating any number.
              Amplitude scales with the risk penalty; a calm flatline reads
              as "on track" for green-band users. */}
          <div className="flex items-center justify-center border-y border-sa-forest/10 px-8 py-5 md:border-x md:border-y-0 md:px-10 md:py-6">
            <LiverAgeWave penalty={data.penalty} bandHex={bandHex} isFlat={isFlat} />
          </div>

          {/* SuperAge Liver Age result */}
          <div className="px-6 py-6 text-right md:px-8 md:py-7">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-sa-copper">
              SuperAge Liver Age
            </p>
            <div className="mt-2 flex items-baseline justify-end gap-2">
              <span className="text-sm text-sa-muted">≈</span>
              <span
                className="font-display font-semibold leading-none"
                style={{ fontSize: "3rem", color: isFlat ? "#0A2E22" : bandHex }}
                data-testid="liver-age-value"
              >
                {data.liverAge}
              </span>
              <span className="text-sm text-sa-muted">yrs</span>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-3 text-[11px] italic leading-relaxed text-sa-muted">
        *{LIVER_AGE_DISCLAIMER}
      </p>
    </div>
  );
}



/** BMI band strip + Basal Metabolic Rate card — shown on the results page.
 *  BMR uses the Revised Harris-Benedict Equation (Mifflin-Roifecke variant):
 *    Men:   BMR = (13.397·kg) + (4.799·cm) − (5.677·age) + 88.362
 *    Women: BMR = ( 9.247·kg) + (3.098·cm) − (4.330·age) + 447.593
 *  For "Other" gender we average the two so we always show something useful.
 */
function BmiTargetStrip({ heightCm, weightKg }) {
  const t = computeTargetWeight(heightCm, weightKg);
  if (!t) return null;
  const isMaintain = t.action === "maintain";
  const verb = t.action === "lose" ? "Lose" : t.action === "gain" ? "Gain" : null;
  const accent = isMaintain ? "#22C55E" : "#C67B5C";
  return (
    <div
      className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-sa-beige px-3.5 py-2.5"
      style={{ backgroundColor: `${accent}10` }}
      data-testid="bmi-target"
    >
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sa-copper">
          Target weight
        </p>
        <p className="mt-0.5 font-display text-lg font-medium leading-none text-sa-forest" data-testid="bmi-target-kg">
          ~{t.targetKg} <span className="text-xs text-sa-muted">kg</span>
        </p>
      </div>
      {isMaintain ? (
        <span
          className="rounded-full px-3 py-1 text-[11px] font-semibold text-white"
          style={{ backgroundColor: accent }}
          data-testid="bmi-target-action"
        >
          Maintain — you're in a healthy range
        </span>
      ) : (
        <div className="text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sa-copper">{verb}</p>
          <p
            className="mt-0.5 font-display text-lg font-medium leading-none"
            style={{ color: accent }}
            data-testid="bmi-target-action"
          >
            ~{t.deltaKg} <span className="text-xs text-sa-muted">kg</span>
          </p>
        </div>
      )}
    </div>
  );
}

function BmiBmrCard({ contact }) {
  const bmi = contact?.bmiValue;
  const heightCm = contact?.heightCm;
  const weightKg = contact?.weightKg;
  const age = contact?.ageYears;
  const gender = (contact?.gender || "").toLowerCase();

  const band = bmi != null ? bmiBandFor(bmi) : null;
  const pointerPct = bmi == null ? 0
    : Math.max(0, Math.min(100, ((bmi - BMI_MIN_DISPLAY) / (BMI_MAX_DISPLAY - BMI_MIN_DISPLAY)) * 100));

  let bmr = null;
  if (heightCm && weightKg && age) {
    const male = (13.397 * weightKg) + (4.799 * heightCm) - (5.677 * age) + 88.362;
    const female = (9.247 * weightKg) + (3.098 * heightCm) - (4.330 * age) + 447.593;
    bmr = gender === "female" ? female
        : gender === "male" ? male
        : Math.round((male + female) / 2);
    bmr = Math.round(bmr);
  }

  if (bmi == null && bmr == null) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="result-bmi-bmr">
      {/* BMI band card */}
      {bmi != null && (
        <div className="rounded-3xl border border-sa-beige bg-white p-5">
          <div className="flex items-baseline justify-between">
            <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-sa-copper">Body mass index</p>
            <span
              className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-white"
              style={{ backgroundColor: band.hex }}
            >
              {band.label}
            </span>
          </div>
          <p className="mt-2 font-display text-4xl font-medium text-sa-forest">{bmi.toFixed(1)}</p>
          <p className="text-[12px] text-sa-muted">
            {heightCm ? `${heightCm.toFixed(0)} cm` : ""}{heightCm && weightKg ? " · " : ""}{weightKg ? `${weightKg.toFixed(0)} kg` : ""}
          </p>
          <div className="mt-3">
            <div className="relative h-2.5 w-full overflow-hidden rounded-full">
              <div className="absolute inset-0 flex">
                {BMI_BANDS.map((b) => {
                  const w = ((Math.min(b.max, BMI_MAX_DISPLAY) - Math.max(b.min, BMI_MIN_DISPLAY)) /
                    (BMI_MAX_DISPLAY - BMI_MIN_DISPLAY)) * 100;
                  return <div key={b.label} style={{ width: `${w}%`, backgroundColor: b.hex }} />;
                })}
              </div>
              <div
                className="pointer-events-none absolute -top-0.5 h-4 w-1 rounded-full bg-sa-forest shadow"
                style={{ left: `calc(${pointerPct}% - 2px)` }}
              />
            </div>
            <div className="mt-1.5 grid grid-cols-5 text-center text-[9px] font-medium uppercase tracking-wider text-sa-forest/70">
              <span>Under</span><span>Normal</span><span>Over</span><span>Obese</span><span>Morbid</span>
            </div>
          </div>
          <BmiTargetStrip heightCm={heightCm} weightKg={weightKg} />
        </div>
      )}

      {/* BMR card */}
      <div className="rounded-3xl border border-sa-beige bg-sa-forest p-5 text-white">
        <div className="flex items-baseline justify-between">
          <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-sa-copper">Basal metabolic rate</p>
          <span className="text-[11px] font-medium text-white/60">Harris–Benedict</span>
        </div>
        {bmr != null ? (
          <>
            <p className="mt-2 font-display text-4xl font-medium">
              {bmr.toLocaleString()} <span className="text-lg text-white/70">kcal/day</span>
            </p>
            <p className="text-[12px] text-white/60">
              Calories your body burns at rest — sedentary target = BMR × 1.2
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {[
                { l: "Sedentary", m: 1.2 },
                { l: "Moderate",  m: 1.55 },
                { l: "Active",    m: 1.725 },
              ].map((t) => (
                <div key={t.l} className="rounded-xl bg-white/10 py-1.5">
                  <p className="font-display text-sm font-medium">{Math.round(bmr * t.m).toLocaleString()}</p>
                  <p className="text-[10px] uppercase tracking-wider text-white/60">{t.l}</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="mt-4 text-sm text-white/60">Age or size not captured — BMR unavailable.</p>
        )}
      </div>
    </div>
  );
}

/** Minimalist reference table shown under a question — displays the latest
 *  clinical guideline ranges (ADA, AHA, NLA…) as a clean color-coded table.
 *  Uses the shape defined in questions.js:
 *    { source, cols: [str…], rows: [{ label, values: [str…], color }], footnote? }
 */
function ReferenceTable({ ref_ }) {
  if (!ref_ || !ref_.rows?.length) return null;
  return (
    <div className="mt-6 rounded-2xl border border-sa-beige bg-white p-4" data-testid="question-reference">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sa-copper">
          Reference ranges
        </p>
        <span className="text-[11px] font-medium text-sa-muted">{ref_.source}</span>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-left text-[12.5px]">
          <thead>
            <tr className="text-[10px] font-semibold uppercase tracking-wider text-sa-muted">
              <th className="pb-1.5 pr-3">Category</th>
              {ref_.cols.map((c) => (
                <th key={c} className="pb-1.5 pr-3">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ref_.rows.map((r) => (
              <tr key={r.label} className="border-t border-sa-beige">
                <td className="py-1.5 pr-3">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: r.color }} />
                    <span className="font-medium text-sa-forest">{r.label}</span>
                  </span>
                </td>
                {r.values.map((v, i) => (
                  <td key={i} className="py-1.5 pr-3 text-sa-forest/85">{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ref_.footnote && (
        <p className="mt-2 text-[10.5px] italic text-sa-muted">{ref_.footnote}</p>
      )}
    </div>
  );
}


function BmiInput({ selected, onSelect, onBmiValue, onSizeValue }) {
  const [ft, setFt] = useState("");
  const [inch, setInch] = useState("");
  const [kg, setKg] = useState("");

  const heightM = ((parseFloat(ft) || 0) * 12 + (parseFloat(inch) || 0)) * 0.0254;
  const weight = parseFloat(kg) || 0;
  const bmi = heightM > 0.5 && weight > 0 ? weight / (heightM * heightM) : null;

  useEffect(() => {
    if (bmi == null) return;
    // Scoring buckets (unchanged for backend) — a: <23, b: 23-24.9, c: ≥25
    const id = bmi < 23 ? "a" : bmi < 25 ? "b" : "c";
    if (id !== selected) onSelect(id);
    if (onBmiValue) onBmiValue(Number(bmi.toFixed(2)));
    if (onSizeValue) onSizeValue({ height_cm: Number((heightM * 100).toFixed(1)), weight_kg: Number(weight.toFixed(1)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bmi]);

  const band = bmi == null ? null : bmiBandFor(bmi);
  const pointerPct = bmi == null ? 0
    : Math.max(0, Math.min(100, ((bmi - BMI_MIN_DISPLAY) / (BMI_MAX_DISPLAY - BMI_MIN_DISPLAY)) * 100));

  const field =
    "w-full rounded-2xl border border-white bg-white px-5 py-4 text-sa-forest outline-none transition-colors focus:border-sa-copper";

  return (
    <div className="mt-8 space-y-5" data-testid="bmi-input">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-sa-forest">Height</label>
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <input value={ft} onChange={(e) => setFt(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="Feet" data-testid="bmi-feet" className={field} />
            <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm text-sa-muted">ft</span>
          </div>
          <div className="relative">
            <input value={inch} onChange={(e) => setInch(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="numeric" placeholder="Inches" data-testid="bmi-inches" className={field} />
            <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm text-sa-muted">in</span>
          </div>
        </div>
        {heightM > 0.5 && (
          <p className="mt-1.5 text-xs text-sa-muted" data-testid="bmi-height-cm-ref">
            <span className="font-medium text-sa-forest">≈ {(heightM * 100).toFixed(0)} cm</span>
            <span className="mx-1.5">·</span>
            <span>{((parseFloat(ft) || 0) * 12 + (parseFloat(inch) || 0)).toFixed(0)} inches total</span>
          </p>
        )}
      </div>
      <div>
        <label className="mb-1.5 block text-sm font-medium text-sa-forest">Weight</label>
        <div className="relative">
          <input value={kg} onChange={(e) => setKg(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="Weight" data-testid="bmi-weight" className={field} />
          <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm text-sa-muted">kg</span>
        </div>
      </div>

      {/* BMI card — number + full 5-band spectrum */}
      <div
        className="rounded-2xl border bg-white px-5 py-5"
        style={{ borderColor: band ? `${band.hex}55` : "#E7E1D5" }}
        data-testid="bmi-result"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-sa-forest">Your BMI</span>
          {bmi == null ? (
            <span className="text-sm text-sa-muted">Enter height & weight</span>
          ) : (
            <span className="flex items-center gap-2">
              <span className="font-display text-2xl font-medium text-sa-forest">{bmi.toFixed(1)}</span>
              <span
                className="rounded-full px-2.5 py-0.5 text-[12px] font-semibold text-white"
                style={{ backgroundColor: band.hex }}
              >
                {band.label}
              </span>
            </span>
          )}
        </div>

        {/* Segmented 5-band strip */}
        <div className="mt-4">
          <div className="relative h-3 w-full overflow-hidden rounded-full">
            <div className="absolute inset-0 flex">
              {BMI_BANDS.map((b) => {
                const w = ((Math.min(b.max, BMI_MAX_DISPLAY) - Math.max(b.min, BMI_MIN_DISPLAY)) /
                  (BMI_MAX_DISPLAY - BMI_MIN_DISPLAY)) * 100;
                return (
                  <div key={b.label} style={{ width: `${w}%`, backgroundColor: b.hex }} />
                );
              })}
            </div>
            {/* Pointer marker */}
            {bmi != null && (
              <div
                className="pointer-events-none absolute -top-1 h-5 w-1 rounded-full bg-sa-forest shadow-md"
                style={{ left: `calc(${pointerPct}% - 2px)` }}
              />
            )}
          </div>
          {/* Labels beneath the strip */}
          <div className="mt-2 grid grid-cols-5 gap-1 text-center text-[10px] font-medium uppercase tracking-wide text-sa-forest/70">
            <span>Under</span>
            <span>Normal</span>
            <span>Over</span>
            <span>Obese</span>
            <span>Morbid</span>
          </div>
          <div className="mt-1 grid grid-cols-5 gap-1 text-center text-[10px] text-sa-muted">
            <span>&lt; 18.5</span>
            <span>18.5–22.9</span>
            <span>23–24.9</span>
            <span>25–29.9</span>
            <span>≥ 30</span>
          </div>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-sa-muted">
          Bands follow the WHO Asia-Pacific / Indian consensus — stricter than global BMI cutoffs because South Asians carry more visceral fat at lower BMIs.
        </p>
      </div>
    </div>
  );
}

function AgeInput({ selected, onSelect, value, onChangeYears, min = 18, max = 100 }) {
  const [years, setYears] = useState(value != null ? String(value) : "");

  useEffect(() => {
    if (value != null && String(value) !== years) setYears(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const n = parseInt(years, 10);
  const valid = Number.isFinite(n) && n >= min && n <= max;
  const bucket = !valid ? null : n < 35 ? "a" : n < 45 ? "b" : n < 55 ? "c" : "d";
  const bandLabel = !valid ? "" : n < 35 ? "Under 35" : n < 45 ? "35–44" : n < 55 ? "45–54" : "55+";
  const bandHex = !valid ? "#8CA095" : n < 35 ? "#22C55E" : n < 45 ? "#EAB308" : n < 55 ? "#F97316" : "#EF4444";

  useEffect(() => {
    if (!valid) return;
    if (bucket !== selected) onSelect(bucket);
    if (n !== value) onChangeYears(n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, valid, bucket]);

  const field =
    "w-full rounded-2xl border border-white bg-white px-5 py-4 text-sa-forest outline-none transition-colors focus:border-sa-copper";

  return (
    <div className="mt-8 space-y-5" data-testid="age-input">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-sa-forest">Age (in years)</label>
        <div className="relative">
          <input
            value={years}
            onChange={(e) => setYears(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
            inputMode="numeric"
            placeholder="e.g. 42"
            data-testid="age-years"
            className={field}
          />
          <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-sm text-sa-muted">yrs</span>
        </div>
        {years && !valid && (
          <p className="mt-2 text-[13px] text-sa-muted">
            Please enter an age between {min} and {max}.
          </p>
        )}
      </div>

      <div
        className="flex items-center justify-between rounded-2xl border px-5 py-4"
        style={{ borderColor: `${bandHex}66`, backgroundColor: `${bandHex}12` }}
        data-testid="age-result"
      >
        <span className="text-sm font-medium text-sa-forest">Your age band</span>
        {!valid ? (
          <span className="text-sm text-sa-muted">Enter your age</span>
        ) : (
          <span className="flex items-center gap-2">
            <span className="font-display text-2xl font-medium text-sa-forest">{n}</span>
            <span
              className="rounded-full px-2.5 py-0.5 text-[12px] font-semibold text-white"
              style={{ backgroundColor: bandHex }}
            >
              {bandLabel}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

/** SplashCard — pre-assessment intro shown as the first screen on /assessment.
 *  Doubles as the QR-code landing page for offline campaigns. Sets up the
 *  value proposition ("2-minute liver score") and the trust story before the
 *  visitor has to type anything.
 */
function SplashCard({ onStart, language = "en", onLanguageChange }) {
  const stats = [
    { big: "1 in 3", label: "Indian adults has fatty liver." },
    { big: "2 min", label: "No blood test needed today." },
    { big: "Precision", label: "Evidence-based." },
  ];
  // Live impact counters — same source as the landing "Our impact" section.
  const [live, setLive] = useState({ assessed: 11434, healthy: 7540, at_risk: 3894 });
  useEffect(() => {
    let alive = true;
    getImpactStats()
      .then((d) => { if (alive && d) setLive((v) => ({ ...v, ...d })); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const liveCounters = [
    { label: "Livers assessed", value: live.assessed },
    { label: "Healthy livers", value: live.healthy },
    { label: "At-risk livers", value: live.at_risk },
  ];
  const LANGS = [
    { id: "en", label: "English", native: "English" },
    { id: "hi", label: "Hindi", native: "हिंदी" },
    { id: "gu", label: "Gujarati", native: "ગુજરાતી" },
  ];
  return (
    <motion.div
      key="splash"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
      data-testid="assessment-splash"
    >
      <div className="rounded-[36px] border border-sa-beige bg-white/70 p-8 backdrop-blur sm:p-12">
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.4 }}
          className="inline-flex items-center gap-2 rounded-full border border-sa-beige bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-sa-copper"
        >
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2.2} />
          Free Liver Assessment
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.55 }}
          className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-sa-forest sm:text-5xl lg:text-[52px]"
        >
          Know your <span className="italic text-sa-copper">liver risk</span>
          <br />
          in 2 minutes.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mt-4 max-w-xl text-[15px] leading-relaxed text-sa-muted"
        >
          Built by <strong className="font-semibold text-sa-forest">Leading Liver Experts</strong>. Backed by clinical evidence.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24, duration: 0.5 }}
          className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2"
          data-testid="splash-live-counters"
        >
          <span className="relative flex h-2 w-2" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          {liveCounters.map((c, i) => (
            <span key={c.label} className="flex items-baseline gap-1.5" data-testid={`splash-live-${i}`}>
              <span className="font-display text-lg font-semibold leading-none text-sa-copper">
                {(c.value ?? 0).toLocaleString("en-IN")}+
              </span>
              <span className="text-[12px] leading-none text-sa-muted">{c.label}</span>
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28, duration: 0.55 }}
          className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3"
        >
          {stats.map((s, i) => (
            <div
              key={s.big}
              className="rounded-2xl border border-sa-beige bg-white/60 px-5 py-4"
              data-testid={`splash-stat-${i}`}
            >
              <p className="font-display text-3xl font-medium leading-none text-sa-forest">
                {s.big}
              </p>
              <p className="mt-2 text-[12.5px] leading-snug text-sa-muted">{s.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32, duration: 0.5 }}
          className="mt-8"
          data-testid="splash-language-picker"
        >
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-sa-copper">
            Take assessment in
          </p>
          <div className="mt-2.5 inline-flex rounded-full border border-sa-beige bg-white p-1">
            {LANGS.map((l) => {
              const active = language === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => onLanguageChange && onLanguageChange(l.id)}
                  data-testid={`splash-language-${l.id}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold transition-all ${
                    active
                      ? "bg-sa-forest text-white shadow-sm"
                      : "text-sa-forest/60 hover:text-sa-forest"
                  }`}
                >
                  <span>{l.native}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.36, duration: 0.5 }}
          className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
        >
          <button
            type="button"
            onClick={onStart}
            className="group inline-flex items-center gap-3 rounded-full bg-sa-forest px-7 py-4 text-[15px] font-semibold text-white shadow-lg shadow-sa-forest/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-sa-forest/25"
            data-testid="splash-start-cta"
          >
            Start My Assessment
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-1"
              strokeWidth={2.4}
            />
          </button>
          <p className="text-[12px] text-sa-muted">10 questions · Private · Free · Instant report</p>
        </motion.div>
      </div>
    </motion.div>
  );
}


function IntroCard({ contact, setContact, onStart }) {
  const upd = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
  const valid =
    contact.firstName.trim() &&
    contact.lastName.trim() &&
    contact.gender &&
    contact.state.trim() &&
    contact.city.trim() &&
    contact.phone.trim();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -60 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid="intro-card"
    >
      <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.2em] text-sa-copper sm:mb-3 sm:text-[13px]">
        Free 2-minute assessment
      </p>
      <h2 className="font-display text-2xl font-medium leading-[1.15] tracking-tight text-sa-forest sm:text-4xl">
        Let's start with a few details
      </h2>
      <p className="mt-2 text-[13px] text-sa-muted sm:mt-3 sm:text-base">
        Private and confidential — we'll never spam you.
      </p>

      <div className="mt-5 space-y-3 sm:mt-8 sm:space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="mb-1 block text-[13px] font-medium text-sa-forest sm:text-sm">First name</label>
            <input
              value={contact.firstName}
              onChange={upd("firstName")}
              placeholder="First name"
              data-testid="intro-first-name"
              className="w-full rounded-2xl border border-white bg-white px-4 py-3 text-sa-forest outline-none transition-colors focus:border-sa-copper sm:px-5 sm:py-4"
            />
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-medium text-sa-forest sm:text-sm">Last name</label>
            <input
              value={contact.lastName}
              onChange={upd("lastName")}
              placeholder="Last name"
              data-testid="intro-last-name"
              className="w-full rounded-2xl border border-white bg-white px-4 py-3 text-sa-forest outline-none transition-colors focus:border-sa-copper sm:px-5 sm:py-4"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-[13px] font-medium text-sa-forest sm:text-sm">Gender</label>
          <div className="flex gap-2 sm:gap-3" data-testid="intro-gender">
            {["Male", "Female", "Other"].map((g) => {
              const active = contact.gender === g;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setContact((c) => ({ ...c, gender: g }))}
                  data-testid={`intro-gender-${g.toLowerCase()}`}
                  className={`flex-1 rounded-2xl border px-4 py-2.5 text-[14px] font-medium transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 sm:px-6 sm:py-3 sm:text-[15px] ${
                    active
                      ? "border-sa-copper bg-sa-copper/10 text-sa-forest"
                      : "border-white bg-white text-sa-forest/70"
                  }`}
                >
                  {g}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="mb-1 block text-[13px] font-medium text-sa-forest sm:text-sm">
              State
            </label>
            <LocationAutocomplete
              value={contact.state}
              onChange={upd("state")}
              options={INDIAN_STATES}
              testId="intro-state"
              ariaLabel="State"
              placeholder="Start typing…"
            />
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-medium text-sa-forest sm:text-sm">
              City
            </label>
            <LocationAutocomplete
              value={contact.city}
              onChange={upd("city")}
              options={CITIES_BY_STATE[contact.state] || ALL_CITIES}
              testId="intro-city"
              ariaLabel="City"
              placeholder="Start typing…"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          <div>
            <label className="mb-1 block text-[13px] font-medium text-sa-forest sm:text-sm">Phone number</label>
            <input
              value={contact.phone}
              onChange={upd("phone")}
              inputMode="tel"
              placeholder="+91 ..."
              data-testid="intro-phone"
              className="w-full rounded-2xl border border-white bg-white px-4 py-3 text-sa-forest outline-none transition-colors focus:border-sa-copper sm:px-5 sm:py-4"
            />
          </div>
          <div>
            <label className="mb-1 block text-[13px] font-medium text-sa-forest sm:text-sm">
              Email <span className="font-normal text-sa-muted">(optional)</span>
            </label>
            <input
              type="email"
              value={contact.email}
              onChange={upd("email")}
              placeholder="you@email.com"
              data-testid="intro-email"
              className="w-full rounded-2xl border border-white bg-white px-4 py-3 text-sa-forest outline-none transition-colors focus:border-sa-copper sm:px-5 sm:py-4"
            />
          </div>
        </div>
      </div>

      <button
        onClick={onStart}
        disabled={!valid}
        data-testid="intro-start"
        className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sa-copper px-7 py-3.5 font-medium text-white transition-[transform,background-color,opacity] duration-300 hover:scale-[1.02] hover:bg-sa-copper-deep disabled:cursor-not-allowed disabled:opacity-40 sm:mt-8 sm:w-auto"
      >
        Start assessment
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </motion.div>
  );
}

function OtpCard({ phone, onVerify, onResend, onChangePhone }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(30);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(phone || "");
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const inputs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (!editingPhone) inputs[0].current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPhone]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const setAt = (i, v) => {
    const next = [...digits];
    next[i] = v;
    setDigits(next);
  };

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setAt(i, "");
      return;
    }
    if (raw.length > 1) {
      // pasted / autofill multiple digits — spread across
      const chars = raw.slice(0, 4 - i).split("");
      const next = [...digits];
      chars.forEach((c, k) => (next[i + k] = c));
      setDigits(next);
      const jumpTo = Math.min(i + chars.length, 3);
      inputs[jumpTo].current?.focus();
      maybeVerify(next);
      return;
    }
    setAt(i, raw);
    if (i < 3) inputs[i + 1].current?.focus();
    if (i === 3) {
      const next = [...digits];
      next[i] = raw;
      maybeVerify(next);
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputs[i - 1].current?.focus();
    }
  };

  const maybeVerify = async (arr) => {
    const code = arr.join("");
    if (code.length !== 4) return;
    setVerifying(true);
    try {
      await onVerify(code);
    } catch {
      // clear digits so user can retry
      setDigits(["", "", "", ""]);
      inputs[0].current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const maskedPhone = phone ? phone.replace(/(\+?\d{2,3})(\d+)(\d{2})/, "$1••••••$3") : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -60 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid="otp-card"
    >
      <p className="mb-3 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
        One last step
      </p>
      <h2 className="font-display text-3xl font-medium leading-[1.12] tracking-tight text-sa-forest sm:text-4xl">
        Verify your phone to see your results
      </h2>
      <p className="mt-3 text-sa-muted">
        We've sent a 4-digit code to <span className="font-medium text-sa-forest" data-testid="otp-masked-phone">{maskedPhone}</span>.
        Enter it below to view your personalized Liver Risk Report.
      </p>

      {editingPhone ? (
        <div
          className="mt-4 flex flex-col gap-3 rounded-2xl border border-sa-beige bg-white/70 p-4 sm:flex-row sm:items-center"
          data-testid="otp-change-phone"
        >
          <input
            type="tel"
            value={phoneDraft}
            onChange={(e) => setPhoneDraft(e.target.value)}
            placeholder="+91 …"
            className="flex-1 rounded-full border border-sa-beige bg-white px-4 py-2.5 text-sa-forest outline-none focus:border-sa-copper"
            data-testid="otp-change-phone-input"
            disabled={updatingPhone}
            autoFocus
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={async () => {
                if (updatingPhone) return;
                const cleaned = phoneDraft.trim();
                if (cleaned.length < 10) {
                  toast.error("Enter a valid phone number");
                  return;
                }
                setUpdatingPhone(true);
                try {
                  await onChangePhone(cleaned);
                  setDigits(["", "", "", ""]);
                  setCooldown(30);
                  setEditingPhone(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e?.message || "Could not update number.");
                } finally {
                  setUpdatingPhone(false);
                }
              }}
              disabled={updatingPhone}
              className="inline-flex items-center gap-2 rounded-full bg-sa-forest px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
              data-testid="otp-change-phone-save"
            >
              {updatingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {updatingPhone ? "Sending…" : "Send new code"}
            </button>
            <button
              type="button"
              onClick={() => {
                setPhoneDraft(phone || "");
                setEditingPhone(false);
              }}
              disabled={updatingPhone}
              className="rounded-full px-4 py-2.5 text-sm font-medium text-sa-muted hover:text-sa-forest disabled:opacity-40"
              data-testid="otp-change-phone-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setPhoneDraft(phone || "");
            setEditingPhone(true);
          }}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-sa-copper/50 bg-sa-copper/10 px-4 py-2 text-[13px] font-semibold text-sa-copper-deep transition-all hover:border-sa-copper hover:bg-sa-copper hover:text-white"
          data-testid="otp-change-phone-toggle"
        >
          <PhoneCall className="h-3.5 w-3.5" strokeWidth={2.4} />
          Wrong number? Change it
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
        </button>
      )}

      <div className="mt-8 flex gap-3" data-testid="otp-inputs">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={inputs[i]}
            value={d}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            inputMode="numeric"
            maxLength={4}
            data-testid={`otp-digit-${i}`}
            disabled={verifying}
            className="h-16 w-14 rounded-2xl border border-white bg-white text-center font-display text-2xl font-medium text-sa-forest outline-none transition-colors focus:border-sa-copper sm:h-20 sm:w-16 sm:text-3xl"
          />
        ))}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={() => maybeVerify(digits)}
          disabled={digits.join("").length !== 4 || verifying}
          data-testid="otp-verify"
          className="group inline-flex items-center gap-2 rounded-full bg-sa-copper px-7 py-3.5 font-medium text-white transition-[transform,background-color,opacity] duration-300 hover:scale-[1.02] hover:bg-sa-copper-deep disabled:cursor-not-allowed disabled:opacity-40"
        >
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {verifying ? "Verifying…" : "Verify & see my score"}
          {!verifying && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
        </button>
        <button
          onClick={() => {
            if (cooldown > 0) return;
            setCooldown(30);
            onResend();
          }}
          disabled={cooldown > 0}
          data-testid="otp-resend"
          className="text-sm font-medium text-sa-forest underline-offset-4 transition-colors hover:text-sa-copper hover:underline disabled:cursor-not-allowed disabled:text-sa-muted disabled:no-underline"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>

      <p className="mt-6 text-xs text-sa-muted">
        Didn't get it? Check your SMS, or wait a few seconds for network delivery.
      </p>
    </motion.div>
  );
}

/** Recovery Potential — a score-band → star-rating mapping used in the
 *  results card to communicate reversibility. Kept minimal & inline beside
 *  the risk-band pill. Logic lives in @/lib/recoveryPotential (shared with PDF).
 */
function RecoveryPotentialChip({ score, band }) {
  const rp = recoveryPotential(score, band);
  if (!rp) return null;
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border border-sa-beige bg-white px-3 py-1 shadow-[0_1px_0_rgba(10,46,34,0.02)]"
      data-testid="recovery-potential"
      title={`Recovery Potential: ${rp.label}`}
    >
      <span className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-sa-muted">
        Recovery
      </span>
      <span className="flex items-center gap-0.5" aria-label={`${rp.stars} out of 5 stars`}>
        {[0, 1, 2, 3, 4].map((i) => {
          const filled = i < rp.stars;
          return (
            <Star
              key={i}
              className={filled ? "h-3.5 w-3.5 text-sa-copper" : "h-3.5 w-3.5 text-sa-beige"}
              fill={filled ? "#C67B5C" : "none"}
              strokeWidth={filled ? 1.6 : 1.5}
            />
          );
        })}
      </span>
      <span className="text-[12px] font-semibold text-sa-forest" data-testid="recovery-potential-label">
        {rp.label}
      </span>
    </span>
  );
}

function ResultCard({ result, contact, answers, onDownload, onBook, onShare }) {
  const band = RISK_BANDS[result.band];
  const circumference = 2 * Math.PI * 52;
  const dash = (result.score / (result.max || 1)) * circumference;
  const isHighRisk = result.band === "orange" || result.band === "red";
  const isEmergency = result.red_flag_level === "emergency";
  const isUrgentFlag = result.red_flag_level === "urgent" || result.red_flag_level === "clinical";
  const cta = CTA_BY_BAND[result.band]; // null for green
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="result-card"
    >
      {isEmergency && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-start gap-4 rounded-2xl border-2 border-red-500 bg-red-50 p-5"
          data-testid="emergency-banner"
        >
          <AlertTriangle className="h-6 w-6 flex-none text-red-600" strokeWidth={2.2} />
          <div>
            <p className="font-display text-lg font-semibold text-red-700">
              Emergency medical evaluation is required now
            </p>
            <p className="mt-1 text-sm leading-relaxed text-red-900/80">
              Your responses include symptoms that can signal a medical emergency. Please
              proceed to the nearest emergency department or call emergency services
              immediately.
            </p>
          </div>
        </motion.div>
      )}
      {!isEmergency && isUrgentFlag && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-start gap-4 rounded-2xl border border-orange-400 bg-orange-50 p-5"
          data-testid="urgent-banner"
        >
          <AlertTriangle className="h-6 w-6 flex-none text-orange-600" strokeWidth={2} />
          <div>
            <p className="font-display text-lg font-semibold text-orange-800">
              Prompt medical assessment is required
            </p>
            <p className="mt-1 text-sm leading-relaxed text-orange-900/80">
              You have reported one or more medical red flags. A physician-led
              Precision Liver Evaluation is strongly recommended within the next
              48–72 hours.
            </p>
          </div>
        </motion.div>
      )}
      {!isEmergency && !isUrgentFlag && result.band === "red" && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 overflow-hidden rounded-2xl border-2 border-red-500 bg-gradient-to-r from-red-50 via-red-100 to-red-50 p-5 shadow-[0_10px_28px_-14px_rgba(239,68,68,0.55)]"
          data-testid="red-band-banner"
        >
          <div className="flex items-start gap-4">
            <span className="relative flex h-11 w-11 flex-none items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-40" />
              <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-600">
                <AlertTriangle className="h-5 w-5 text-white" strokeWidth={2.4} />
              </span>
            </span>
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-red-700">
                Very high risk
              </p>
              <p className="mt-1 font-display text-lg font-semibold text-red-800 sm:text-xl">
                Immediate clinical attention recommended
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-red-900/80">
                Your responses place you in the highest-risk band. Please book a
                physician-led Precision Liver Evaluation within the next 48–72 hours to
                confirm liver status and begin a personalized recovery plan.
              </p>
            </div>
          </div>
        </motion.div>
      )}
      <div className="rounded-[32px] border border-white bg-white p-8 md:p-10">
        <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-center">
          <div className="relative flex-none">
            <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
              <circle cx="70" cy="70" r="52" fill="none" stroke="#EBE8E0" strokeWidth="12" />
              <motion.circle
                cx="70" cy="70" r="52" fill="none" stroke={band.hex} strokeWidth="12" strokeLinecap="round"
                strokeDasharray={circumference}
                initial={{ strokeDashoffset: circumference }}
                animate={{ strokeDashoffset: circumference - dash }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-4xl font-medium text-sa-forest" data-testid="result-score">
                {result.score}
              </span>
              <span className="text-[11px] font-medium uppercase tracking-wider text-sa-muted">/ 100</span>
            </div>
          </div>

          <div className="text-center sm:text-left">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[13px] font-semibold text-white"
                style={{ backgroundColor: band.hex }}
                data-testid="result-band"
              >
                {result.label}
              </span>
              <RecoveryPotentialChip score={result.score} band={result.band} />
            </div>
            <h2 className="mt-3 font-display text-3xl font-medium leading-[1.12] tracking-tight text-sa-forest">
              {result.headline}
            </h2>
            <p className="mt-2 text-sa-muted">{result.summary}</p>
          </div>
        </div>

        <LiverAgeCard result={result} contact={contact} />

        <div className="mt-8 border-t border-sa-beige pt-8">
          <BmiBmrCard contact={contact} />
        </div>

        <div className="mt-8 border-t border-sa-beige pt-8">
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
            Your top risk contributors
          </p>
          {result.top_contributors && result.top_contributors.length > 0 ? (
            <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="result-contributors">
              {result.top_contributors.map((c, i) => (
                <motion.li
                  key={c.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.08 }}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-sa-beige bg-sa-beige/60 px-4 py-3"
                >
                  <span>
                    <span className="block text-[13px] text-sa-muted">{c.question}</span>
                    <span className="mt-0.5 block text-[15px] font-medium text-sa-forest">{c.answer}</span>
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-[13px] font-semibold text-white"
                    style={{ backgroundColor: band.hex }}
                  >
                    +{c.points}
                  </span>
                </motion.li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sa-muted">
              No standout risk drivers — your current lifestyle is doing the heavy lifting.
            </p>
          )}
        </div>

        {result.next_step && (
          <div
            className="mt-8 rounded-2xl border-2 p-6"
            style={{ borderColor: band.hex, backgroundColor: `${band.hex}0d` }}
            data-testid="result-next-step"
          >
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: band.hex }}>
              Recommended next step
            </p>
            <p className="mt-2 font-display text-xl font-medium text-sa-forest">
              {result.next_step}
            </p>
            {result.recheck && (
              <p className="mt-2 text-sm leading-relaxed text-sa-muted">{result.recheck}</p>
            )}
          </div>
        )}

        {result.recovery_plan ? (
          <RecoveryPlanCard plan={result.recovery_plan} band={band} />
        ) : (
          <div className="mt-8 border-t border-sa-beige pt-8">
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
              Your personalized recommendations
            </p>
            <ul className="mt-5 space-y-3" data-testid="result-recommendations">
              {result.recommendations.map((r, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <span
                    className="mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: band.hex }}
                  >
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="text-sa-forest">{r}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        )}

        {isHighRisk && !isEmergency && !isUrgentFlag && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-8 overflow-hidden rounded-[24px] border p-6"
            style={{ borderColor: `${band.hex}55`, backgroundColor: `${band.hex}12` }}
            data-testid="result-urgent-nudge"
          >
            <div className="flex items-start gap-4">
              <span
                className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl text-white"
                style={{ backgroundColor: band.hex }}
              >
                <ShieldAlert className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <p className="font-display text-lg font-medium text-sa-forest">
                  Don't wait on this one.
                </p>
                <p className="mt-1 text-sm leading-relaxed text-sa-muted">
                  Your score places you in the {result.label.toLowerCase()} range. A
                  physician-led Precision Liver Evaluation is the fastest way to know
                  exactly what's happening — and most early damage is still reversible.
                </p>
                <button
                  onClick={onBook}
                  data-testid="urgent-book-now"
                  className="group mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white transition-transform duration-300 hover:scale-[1.03]"
                  style={{ backgroundColor: band.hex }}
                >
                  <CalendarCheck className="h-4 w-4" />
                  {cta ? cta.action : "Book my assessment"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        <div className="mt-9 space-y-3">
          {cta && (
            <button
              onClick={onBook}
              data-testid="result-book"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-sa-copper px-6 py-4 font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.02] hover:bg-sa-copper-deep"
            >
              <CalendarCheck className="h-4 w-4" />
              {cta.action}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              onClick={onDownload}
              data-testid="result-download"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-sa-forest/25 bg-white px-6 py-3.5 font-medium text-sa-forest transition-[transform,border-color,background-color] duration-300 hover:scale-[1.02] hover:border-sa-forest/60"
            >
              <FileDown className="h-4 w-4" />
              Download my report
            </button>
            <button
              onClick={onShare}
              data-testid="result-share"
              className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium text-white transition-[transform,filter] duration-300 hover:scale-[1.02] hover:brightness-110"
              style={{ backgroundColor: "#25D366" }}
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.2} fill="currentColor" />
              Share on WhatsApp
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function RecoveryPlanCard({ plan, band }) {
  const ICONS = {
    salad: Salad,
    activity: Dumbbell,
    stethoscope: HeartPulse,
    pill: Pill,
    microscope: Microscope,
    "trending-up": TrendingUp,
  };
  return (
    <div className="mt-8 border-t border-sa-beige pt-8" data-testid="recovery-plan">
      <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
        Your Personalized
      </p>
      <h3 className="mt-1 font-display text-2xl font-medium leading-tight tracking-tight text-sa-forest sm:text-3xl">
        Liver Recovery Recommendations
      </h3>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {plan.sections.map((s, idx) => {
          const Icon = ICONS[s.icon] || Sparkles;
          const isFullWidth = idx === 4;
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + idx * 0.06 }}
              data-testid={`recovery-section-${s.key}`}
              className={`rounded-3xl border border-sa-beige bg-white p-5 ${isFullWidth ? "md:col-span-2" : ""}`}
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-sa-copper text-white">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <h4 className="font-display text-lg font-medium text-sa-forest">{s.title}</h4>
              </div>
              <ul className="mt-4 space-y-2.5">
                {s.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span
                      className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full"
                      style={{ backgroundColor: "#C67B5C" }}
                    />
                    <span className="text-[14.5px] leading-relaxed text-sa-forest/85">{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}
